import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@rudi/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { Textarea } from '@rudi/ui/textarea';
import {
  isMissingPracticeRoute,
  useCreatePrescreeningVersion,
  usePracticeCodeSets,
} from '../api/hooks';
import type { PrescreeningQuery, PrescreeningVersion } from '../api/types';
import { practiceErrorText } from './practice-agents';
import { describeRule, editableDocument, parseCriteriaDocument } from './criteria-query';
import { PRESCREENING_UNAVAILABLE, rudiErrorList } from './prescreening';

/**
 * Edit a version as JSON and save it as a new version. Marking a criterion as
 * wrong opens this with that criterion named, and the note becomes its
 * rationale. Saving stops Rudi's designer for this query.
 */
export function CriteriaQueryEditor({
  query,
  version,
  criterionId,
  onClose,
}: {
  query: PrescreeningQuery;
  version: PrescreeningVersion;
  criterionId?: string;
  onClose: () => void;
}) {
  const create = useCreatePrescreeningVersion();
  const practiceCodeSets = usePracticeCodeSets(query.practiceId ?? '', !!query.practiceId);
  const [text, setText] = useState(() => JSON.stringify(editableDocument(version), null, 2));
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<{ message: string; errors: string[] } | null>(null);
  const parsed = useMemo(() => parseCriteriaDocument(text), [text]);
  const marked = criterionId
    ? version.criteria.find(criterion => criterion.id === criterionId)
    : undefined;
  const confirmed = practiceCodeSets.data;
  const canUsePracticeCodeSets =
    !!confirmed && confirmed.version != null && Object.keys(confirmed.codeSets).length > 0;

  const applyPracticeCodeSets = () => {
    if (!parsed.document || !confirmed) return;
    setText(JSON.stringify({ ...parsed.document, codeSets: confirmed.codeSets }, null, 2));
  };

  const save = async (approve: boolean) => {
    if (!parsed.document) return;
    const reason = note.trim();
    setFailure(null);
    try {
      const saved = await create.mutateAsync({
        queryId: query.queryId,
        body: {
          document: parsed.document,
          approve,
          ...(criterionId && reason ? { rationale: { [criterionId]: reason } } : {}),
        },
      });
      const newest = saved.versions[saved.versions.length - 1]?.version;
      const label = newest ? `Version ${newest}` : 'The new version';
      toast.success(approve ? `${label} approved` : `${label} saved as a draft`);
      onClose();
    } catch (error) {
      setFailure({
        message: isMissingPracticeRoute(error, 'write')
          ? PRESCREENING_UNAVAILABLE
          : practiceErrorText(error, 'Could not save the version'),
        errors: rudiErrorList(error),
      });
    }
  };

  const pending = create.isPending;

  return (
    <Dialog open onOpenChange={next => !next && !pending && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>New version from version {version.version}</DialogTitle>
          <DialogDescription>
            Edit the code sets, the candidate threshold and the criteria. Rudi fills in the rest,
            checks the document and signs it. Saving stops Rudi&apos;s designer for this query.
          </DialogDescription>
        </DialogHeader>

        {criterionId && (
          <div className="space-y-2 rounded-3xl border bg-muted/40 p-4 text-sm">
            <div>
              <span className="font-medium">Marked as wrong: {criterionId}</span>
              {marked && <span className="text-muted-foreground"> {marked.text}</span>}
            </div>
            {marked && (
              <div className="text-xs text-muted-foreground">
                Rule now: {describeRule(marked.rule, version.codeSets)}
              </div>
            )}
            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              What was wrong, kept as the criterion&apos;s rationale
              <Textarea
                aria-label="What was wrong"
                value={note}
                maxLength={2000}
                disabled={pending}
                onChange={event => setNote(event.target.value)}
                className="min-h-[60px]"
              />
            </label>
          </div>
        )}

        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          Criteria query, as JSON
          <Textarea
            aria-label="Criteria query JSON"
            value={text}
            spellCheck={false}
            disabled={pending}
            onChange={event => setText(event.target.value)}
            className="min-h-[22rem] font-mono text-xs"
          />
        </label>

        {canUsePracticeCodeSets && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={pending || !parsed.document}
              onClick={applyPracticeCodeSets}
            >
              Use the Practice&apos;s code sets
            </Button>
            Replaces codeSets with version {confirmed?.version} of the Practice&apos;s confirmed
            code sets.
          </div>
        )}

        {parsed.errors.length > 0 && (
          <div role="status" aria-label="Problems" className="text-xs text-destructive">
            <p className="font-medium">Fix these before saving:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {parsed.errors.slice(0, 20).map(error => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {failure && (
          <div role="alert" className="text-xs text-destructive">
            <p className="font-medium">{failure.message}</p>
            {failure.errors.length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {failure.errors.map(error => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="outline"
            disabled={pending || !parsed.document}
            onClick={() => void save(false)}
          >
            Save as draft
          </Button>
          <Button disabled={pending || !parsed.document} onClick={() => void save(true)}>
            Save and approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
